/** SUNTRONIX ONE — Administration → Privacy & DPDP Control Centre.
 *  India DPDP Act 2023 + Rules 2025 READINESS layer. Never asserts compliance —
 *  reports READINESS / CONTROL STATUS / GAPS / ACTION REQUIRED. Admin-only. */
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, Plus, Trash2, Search, AlertTriangle, Clock, FileText } from "lucide-react";

const TABS = [
  ["readiness", "Readiness"], ["requests", "Data Principal Requests"], ["consent", "Consent & Preferences"],
  ["breaches", "Breach Centre"], ["processors", "Processors"], ["activities", "Processing Activities"],
  ["notices", "Privacy Notices"], ["retention", "Retention"], ["crossborder", "Cross-border"],
  ["children", "Children's Data"], ["policies", "Policy Library"], ["settings", "Privacy Contact & Timeline"],
];
const REGISTER_FIELDS = {
  activities: ["name", "module", "suntronix_role", "data_principal_category", "purpose", "processing_ground", "owner"],
  notices: ["title", "audience", "language", "rights_link", "withdrawal_method", "grievance_contact", "status"],
  retention: ["data_category", "purpose", "system", "trigger", "retention_period", "legal_basis", "erasure_action", "owner"],
  crossborder: ["system_processor", "country", "purpose", "data", "transfer_type", "restriction_status"],
  children: ["data_principal", "age_band", "parent_consent_evidence"],
  policies: ["title", "kind", "status"],
};

export default function PrivacyDpdp() {
  const [tab, setTab] = useState("readiness");
  const [meta, setMeta] = useState({ purposes: [], grounds: [], request_statuses: [] });
  useEffect(() => { api.get("/dpdp/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  return (
    <div className="max-w-6xl mx-auto px-4 py-5" data-testid="dpdp-page">
      <div className="flex items-center gap-2 mb-1">
        <span className="h-8 w-8 rounded-lg bg-slate-900 grid place-items-center"><ShieldCheck className="h-4 w-4 text-white" /></span>
        <h1 className="text-lg font-semibold text-slate-900">Privacy &amp; DPDP Control Centre</h1>
      </div>
      <p className="text-[12px] text-slate-500 mb-4">India DPDP Act 2023 + Rules 2025 readiness. Reports readiness &amp; gaps — never asserts legal compliance.</p>
      <div className="flex flex-wrap gap-1.5 mb-4" data-testid="dpdp-tabs">
        {TABS.map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`dpdp-tab-${id}`}
            className={`px-2.5 py-1 rounded-full text-[12px] border ${tab === id ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>{label}</button>
        ))}
      </div>
      {tab === "readiness" && <Readiness />}
      {tab === "requests" && <Requests meta={meta} />}
      {tab === "consent" && <Consent />}
      {tab === "breaches" && <Breaches />}
      {tab === "settings" && <Settings />}
      {["processors", "activities", "notices", "retention", "crossborder", "children", "policies"].includes(tab) &&
        <Register name={tab} meta={meta} />}
    </div>
  );
}

const Readiness = () => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dpdp/readiness").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="text-[13px] text-slate-400">Loading…</div>;
  return (
    <div data-testid="dpdp-readiness">
      <div className={`rounded-lg border px-3 py-2 text-[13px] font-medium mb-3 ${d.gaps.length ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-emerald-50 border-emerald-200 text-emerald-800"}`}>
        {d.status_label} · {d.readiness_percent}% readiness
      </div>
      <div className="text-[11px] text-slate-500 mb-3 italic">{d.disclaimer}</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 mb-4">
        {Object.entries(d.cards).map(([k, v]) => (
          <div key={k} className="rounded-lg border border-slate-200 bg-white px-3 py-2">
            <div className="text-[10px] uppercase text-slate-400">{k.replace(/_/g, " ")}</div>
            <div className="text-base font-semibold text-slate-900">{typeof v === "boolean" ? (v ? "Yes" : "No") : v}</div>
          </div>
        ))}
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-3">
        <div className="text-[12px] font-semibold mb-2">Control status</div>
        {d.controls.map((c) => (
          <div key={c.control} className="flex items-center justify-between text-[12px] border-b border-slate-50 py-1">
            <span>{c.control}</span>
            <Badge className={`text-[10px] border ${c.status === "PASS" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>{c.status.replace(/_/g, " ")}</Badge>
          </div>
        ))}
      </div>
    </div>
  );
};

const Requests = ({ meta }) => {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ requester: "", request_type: "ACCESS" });
  const load = useCallback(() => api.get("/dpdp/requests").then((r) => setRows(r.data.items)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    if (!f.requester) { toast.error("Requester email required"); return; }
    try { await api.post("/dpdp/requests", f); toast.success("Request logged"); load(); } catch { toast.error("Failed"); }
  };
  const act = async (rid, path, ok) => { try { const { data } = await api.post(`/dpdp/requests/${rid}/${path}`); toast.success(ok + (data.recommendation ? `: ${data.recommendation}` : data.total_modules != null ? ` (${data.total_modules} modules)` : "")); load(); } catch { toast.error("Failed"); } };
  return (
    <div data-testid="dpdp-requests">
      <div className="rounded-xl border border-slate-200 bg-white p-3 mb-3 flex flex-wrap gap-2 items-end">
        <Input placeholder="Requester email" value={f.requester} onChange={(e) => setF({ ...f, requester: e.target.value })} data-testid="dpr-requester" className="max-w-xs" />
        <select value={f.request_type} onChange={(e) => setF({ ...f, request_type: e.target.value })} data-testid="dpr-type" className="h-9 border border-slate-200 rounded-md px-2 text-[13px] bg-white">
          {(meta.request_types || ["ACCESS", "ERASURE"]).map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
        </select>
        <Button variant="create" onClick={create} data-testid="dpr-create" className="bg-slate-900 hover:bg-slate-800"><Plus className="h-4 w-4 mr-1" /> Log request</Button>
      </div>
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.id} className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`dpr-${r.id}`}>
            <div className="flex items-center justify-between">
              <div className="text-[13px] font-semibold">{r.request_no} · {r.request_type} <span className="text-slate-400 font-normal">· {r.requester}</span></div>
              <Badge className="text-[10px] border bg-slate-50 text-slate-600 border-slate-200">{r.status.replace(/_/g, " ")}</Badge>
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">Due {(r.due_date || "").slice(0, 10)} · systems: {(r.systems_affected || []).join(", ") || "—"}{r.erasure_recommendation ? ` · ${r.erasure_recommendation}` : ""}</div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              <Button size="sm" variant="outline" onClick={() => act(r.id, "discovery", "Discovery done")} data-testid={`dpr-discovery-${r.id}`}><Search className="h-3.5 w-3.5 mr-1" /> Data discovery</Button>
              {r.request_type === "ERASURE" && <Button size="sm" variant="outline" onClick={() => act(r.id, "erasure-review", "Erasure review")} data-testid={`dpr-erasure-${r.id}`}>Erasure review</Button>}
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="text-[13px] text-slate-400 text-center py-8">No requests yet.</div>}
      </div>
    </div>
  );
};

const Consent = () => {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ data_principal: "", purpose: "", status: "GIVEN", notice_version: "", source: "", evidence: "" });
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const load = useCallback(() => { setLoadError(false); return api.get("/dpdp/consents").then((r) => setRows(r.data.items)).catch(() => setLoadError(true)); }, []);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (busy) return;
    if (!f.data_principal.trim() || !f.purpose.trim()) { toast.error("Data principal and purpose required"); return; }
    if (f.status === "GIVEN" && ![f.notice_version, f.source, f.evidence].every((v) => v.trim())) {
      toast.error("Notice version, source and consent evidence are required"); return;
    }
    setBusy(true);
    try { await api.post("/dpdp/consents", f); toast.success("Consent choice recorded"); await load(); }
    catch { toast.error("Consent was not recorded. Check the evidence and try again."); }
    finally { setBusy(false); }
  };
  const withdraw = async (id) => {
    if (busy) return;
    setBusy(true);
    try { await api.post("/dpdp/consents/" + id + "/withdraw", {}); toast.info("Withdrawal recorded. Processing cessation and processor actions require verification."); await load(); }
    catch { toast.error("Withdrawal could not be recorded"); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="dpdp-consent">
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800 mb-3">Business relationship follow-up is separate from marketing. Recording consent does not by itself enable messaging or prove processing has stopped. This is an internal consent ledger, not a registered DPDP Consent Manager.</div>
      <div className="rounded-xl border border-slate-200 bg-white p-3 mb-3 flex flex-wrap gap-2 items-end">
        <Input placeholder="Data principal (email)" value={f.data_principal} onChange={(e) => setF({ ...f, data_principal: e.target.value })} data-testid="consent-dp" className="max-w-xs" />
        <Input placeholder="Purpose" value={f.purpose} onChange={(e) => setF({ ...f, purpose: e.target.value })} data-testid="consent-purpose" className="max-w-xs" />
        <select aria-label="Consent choice" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className="h-9 border rounded px-2 text-sm"><option value="GIVEN">Given</option><option value="DECLINED">Declined</option></select>
        <Input placeholder="Privacy notice version" aria-label="Privacy notice version" value={f.notice_version} onChange={(e) => setF({ ...f, notice_version: e.target.value })} />
        <Input placeholder="Consent source" aria-label="Consent source" value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} />
        <Input placeholder="Evidence reference (no passwords or identity documents)" aria-label="Consent evidence reference" value={f.evidence} onChange={(e) => setF({ ...f, evidence: e.target.value })} />
        <Button variant="create" disabled={busy} onClick={add} data-testid="consent-add" className="bg-slate-900 hover:bg-slate-800"><Plus className="h-4 w-4 mr-1" /> Record consent</Button>
      </div>
      <div className="space-y-1.5">
        {rows.map((c) => (
          <div key={c.id} className="flex items-center justify-between rounded border border-slate-200 bg-white px-3 py-2 text-[12px]">
            <span>{c.data_principal} · {c.purpose} <Badge className={`ml-1 text-[10px] border ${c.status === "WITHDRAWN" ? "bg-rose-50 text-rose-600 border-rose-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>{c.status}</Badge></span>
            {c.status !== "WITHDRAWN" && <Button size="sm" disabled={busy} variant="outline" onClick={() => withdraw(c.id)} data-testid={`consent-withdraw-${c.id}`}>Withdraw</Button>}
          </div>
        ))}
        {loadError ? <div role="alert" className="text-sm text-rose-700">Unable to load consent records. <button onClick={load}>Retry</button></div> : rows.length === 0 && <div className="text-[13px] text-slate-400 text-center py-8">No consent records.</div>}
      </div>
    </div>
  );
};

const Breaches = () => {
  const [rows, setRows] = useState([]);
  const [title, setTitle] = useState("");
  const load = useCallback(() => api.get("/dpdp/breaches").then((r) => setRows(r.data.items)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const create = async () => { if (!title) { toast.error("Title required"); return; } try { await api.post("/dpdp/breaches", { title, data_categories: [] }); toast.success("Incident logged — 72h clock started (simulation)"); setTitle(""); load(); } catch { toast.error("Failed"); } };
  return (
    <div data-testid="dpdp-breaches">
      <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[11px] text-rose-700 mb-3">Logging "aware" starts the regulatory clock. Simulation only — no actual Board notification is sent.</div>
      <div className="rounded-xl border border-slate-200 bg-white p-3 mb-3 flex gap-2 items-end">
        <Input placeholder="Incident title" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="breach-title" />
        <Button variant="create" onClick={create} data-testid="breach-create" className="bg-rose-600 hover:bg-rose-700"><AlertTriangle className="h-4 w-4 mr-1" /> Log breach</Button>
      </div>
      <div className="space-y-2">
        {rows.map((b) => (
          <div key={b.id} className="rounded-lg border border-slate-200 bg-white p-3 text-[12px]" data-testid={`breach-${b.id}`}>
            <div className="flex items-center justify-between"><span className="font-semibold">{b.incident_no} · {b.title}</span><Badge className="text-[10px] border bg-slate-50 text-slate-600 border-slate-200">{b.status}</Badge></div>
            <div className="text-[11px] text-slate-500 mt-1 flex flex-wrap gap-3">
              <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> 72h deadline {(b.detailed_update_deadline || "").slice(0, 16).replace("T", " ")}{b.hours_remaining != null ? ` (${b.hours_remaining}h left)` : ""}</span>
              <span>Board: {b.board_package_status}</span><span>Affected notice: {b.affected_notice_status}</span>
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="text-[13px] text-slate-400 text-center py-8">No incidents.</div>}
      </div>
    </div>
  );
};

const Settings = () => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dpdp/settings").then((r) => setD(r.data)).catch(() => {}); }, []);
  const save = async () => { try { await api.put("/dpdp/settings", { privacy_contact: d.privacy_contact, effective_date: d.effective_date }); toast.success("Saved"); } catch { toast.error("Failed"); } };
  if (!d) return <div className="text-[13px] text-slate-400">Loading…</div>;
  const pc = d.privacy_contact || {};
  const setPc = (k, v) => setD({ ...d, privacy_contact: { ...pc, [k]: v } });
  return (
    <div data-testid="dpdp-settings" className="grid md:grid-cols-2 gap-4">
      <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-2">
        <div className="text-[12px] font-semibold mb-1">Privacy Contact (§42)</div>
        <Input placeholder="Name" value={pc.name || ""} onChange={(e) => setPc("name", e.target.value)} data-testid="pc-name" />
        <Input placeholder="Business email" value={pc.email || ""} onChange={(e) => setPc("email", e.target.value)} data-testid="pc-email" />
        <Input placeholder="Phone" value={pc.phone || ""} onChange={(e) => setPc("phone", e.target.value)} data-testid="pc-phone" />
        <Input placeholder="Effective date (readiness timeline)" value={d.effective_date || ""} onChange={(e) => setD({ ...d, effective_date: e.target.value })} data-testid="pc-effective" />
        <Button onClick={save} data-testid="settings-save" className="bg-slate-900 hover:bg-slate-800 mt-1">Save</Button>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="text-[12px] font-semibold mb-2">Enforcement Timeline (§2)</div>
        {(d.enforcement || []).map((e, i) => (
          <div key={i} className="flex items-center justify-between text-[12px] border-b border-slate-50 py-1.5">
            <span>{e.obligation}</span>
            <Badge className="text-[10px] border bg-sky-50 text-sky-700 border-sky-200">{e.status.replace(/_/g, " ")}</Badge>
          </div>
        ))}
      </div>
    </div>
  );
};

const Register = ({ name, meta }) => {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});
  const fields = REGISTER_FIELDS[name] || ["name"];
  const load = useCallback(() => api.get(`/dpdp/registers/${name}`).then((r) => setRows(r.data.items)).catch(() => {}), [name]);
  useEffect(() => { load(); setOpen(false); setF({}); }, [load]);
  const create = async () => { try { await api.post(`/dpdp/registers/${name}`, f); toast.success("Added"); setOpen(false); setF({}); load(); } catch { toast.error("Failed"); } };
  const del = async (id) => { if (!window.confirm("Delete this entry?")) return; try { await api.delete(`/dpdp/registers/${name}/${id}`); load(); } catch { toast.error("Failed"); } };
  return (
    <div data-testid={`dpdp-register-${name}`}>
      <div className="flex justify-between items-center mb-2">
        <div className="text-[12px] text-slate-500">{rows.length} entries</div>
        <Button variant="create" size="sm" onClick={() => setOpen(!open)} data-testid={`reg-add-${name}`} className="bg-slate-900 hover:bg-slate-800"><Plus className="h-4 w-4 mr-1" /> Add</Button>
      </div>
      {open && (
        <div className="rounded-xl border border-slate-200 bg-white p-3 mb-3 grid sm:grid-cols-2 gap-2">
          {fields.map((k) => (
            k === "processing_ground" ? (
              <select key={k} value={f[k] || "LEGAL_REVIEW_REQUIRED"} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`reg-${name}-${k}`} className="h-9 border border-slate-200 rounded-md px-2 text-[13px] bg-white">
                {(meta.grounds || []).map((g) => <option key={g.code} value={g.code}>{g.label}</option>)}
              </select>
            ) : (
              <Input key={k} placeholder={k.replace(/_/g, " ")} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`reg-${name}-${k}`} />
            )
          ))}
          <div className="sm:col-span-2"><Button size="sm" onClick={create} data-testid={`reg-save-${name}`} className="bg-indigo-600 hover:bg-indigo-700">Save entry</Button></div>
        </div>
      )}
      <div className="space-y-1.5">
        {rows.map((r) => (
          <div key={r.id} className="flex items-center justify-between rounded border border-slate-200 bg-white px-3 py-2 text-[12px]">
            <span className="truncate">{r.name || r.title || r.vendor || r.data_category || r.system_processor || r.data_principal || r.id}
              {(r.status || r.review_flag) && <Badge className={`ml-2 text-[10px] border ${(r.status === "PROCESSOR_REVIEW_REQUIRED" || r.review_flag || r.restriction_status === "LEGAL_REVIEW_REQUIRED" || r.status === "REVIEW_REQUIRED") ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-slate-50 text-slate-600 border-slate-200"}`}>{r.review_flag || r.status || r.restriction_status}</Badge>}
            </span>
            <button onClick={() => del(r.id)} className="text-rose-500 hover:text-rose-700" data-testid={`reg-del-${r.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        {rows.length === 0 && <div className="text-[13px] text-slate-400 text-center py-8">No entries.</div>}
      </div>
    </div>
  );
};
